import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../../layout";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { ServiceManager } from "../../../services/ServiceManager";
import MISButton from "../../../components/button";
import { Spinner, Stack, Container, Row, Col } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { APIStatusCode, Permissions } from "../../../app/enums";
import { usePermissionCheck } from "../../../app/common/helper/Permissions";

interface IPinSetting {
    settingID?: string;
    pinLength: number;
    maxGenerationAttempt: number;
    autoPinGeneration: boolean;
}

export default function PinSetting(props: any) {

    const [settingId, setSettingId] = useState<string | undefined>(undefined);
    const [pinLength, setPinLength] = useState<number>(6);
    const [maxGenerationAttempt, setMaxGenerationAttempt] = useState<number>(1000000);
    const [autoPinGeneration, setAutoPinGeneration] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ pinLength?: string; maxGenerationAttempt?: string }>({});
    
    const canEditEstablishments = usePermissionCheck(Permissions.CanEditEstablishments);
    const queryClient = useQueryClient();
    let navigate = useNavigate();
    const { establishmentId } = useParams();
    const firstCol = 3, secondCol = 4;
    
    // Fetch existing pin settings from backend
    const { data: pinSetting, isLoading } = useQuery({
        queryKey: [`pinsetting-${establishmentId}`],
        queryFn: async () => {
            try {
                const response = await ServiceManager.PINSettingService.GetPinSetting(establishmentId!);
                // If API returns error or 404, return null to use default values
                if (response?.status === 404 || response?.response?.status === 404) {
                    return null;
                }
                return response;
            } catch (error) {
                // If no settings exist yet, return null to use default values
                return null;
            }
        },
        enabled: !!establishmentId,
        retry: false,
    });

    useEffect(() => {
        if (!isLoading) {
            if (pinSetting && pinSetting.settingID) {
                // Existing settings found
                setSettingId(pinSetting.settingID);
                setPinLength(pinSetting.pinLength);
                setMaxGenerationAttempt(pinSetting.maxGenerationAttempt);
                setAutoPinGeneration(pinSetting.allowAutoPINGeneration || pinSetting.autoPinGeneration);
            } else {
                // No settings found, use defaults
                setSettingId(undefined);
                setPinLength(6);
                setMaxGenerationAttempt(1000000);
                setAutoPinGeneration(false);
            }
        }
    }, [pinSetting, isLoading]);

    const validateForm = (): boolean => {
        const newErrors: { pinLength?: string; maxGenerationAttempt?: string } = {};

        if (!pinLength || pinLength < 4 || pinLength > 10) {
            newErrors.pinLength = "PIN length must be between 4 and 10";
        }

        if (!maxGenerationAttempt || maxGenerationAttempt < 1) {
            newErrors.maxGenerationAttempt = "Max generation attempt must be at least 1";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const saveMutation = useMutation({
        mutationFn: (data: IPinSetting) => {
            if (settingId) {
                // Update existing PIN setting
                return ServiceManager.PINSettingService.UpdatePinSetting(establishmentId!, settingId, data);
            } else {
                // Create new PIN setting
                return ServiceManager.PINSettingService.CreatePinSetting(establishmentId!, data);
            }
        },
        onError: (error: any) => {
            ServiceManager.ToastService.showError("Unable to save pin setting.");
        },
        onSuccess: (data: any) => {
            if (data === null) {
                ServiceManager.ToastService.showError("Unable to save pin setting.");
            } else {
                if (data.status === APIStatusCode.Ok || data.status === APIStatusCode.Created) {
                    ServiceManager.ToastService.showSuccess("Pin setting saved successfully.");
                    // Update settingID if it was just created from the response data
                    if (data.data && data.data.settingID) {
                        setSettingId(data.data.settingID);
                    }
                    // Refetch the pin setting to get the latest data
                    queryClient.invalidateQueries({ queryKey: [`pinsetting-${establishmentId}`] });
                } else {
                    let errorMessage = (data.response && data.response.data && data.response.data.errors.errors) 
                        ? data.response.data.errors.errors 
                        : data.message;
                    ServiceManager.ToastService.showError(errorMessage.toString());
                }
            }
        }
    });

    const handleSave = () => {
        if (!validateForm()) {
            return;
        }

        const pinSettingData: IPinSetting = {
            pinLength,
            maxGenerationAttempt,
            autoPinGeneration
        };

        saveMutation.mutate(pinSettingData);
    };

    const handleCancel = () => {
        if (pinSetting) {
            setSettingId(pinSetting.settingID);
            setPinLength(pinSetting.pinLength);
            setMaxGenerationAttempt(pinSetting.maxGenerationAttempt);
            setAutoPinGeneration(pinSetting.allowAutoPINGeneration || pinSetting.autoPinGeneration);
        }
        setErrors({});
        navigate(-1);
    };

    const handlePinLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        setPinLength(value);
        if (errors.pinLength) {
            setErrors({ ...errors, pinLength: undefined });
        }
    };

    const handleMaxGenerationAttemptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        setMaxGenerationAttempt(value);
        if (errors.maxGenerationAttempt) {
            setErrors({ ...errors, maxGenerationAttempt: undefined });
        }
    };

    return (
        <Layout 
            title="Pin Setting"
            backArrow={
                <FontAwesomeIcon icon={faArrowLeft} onClick={() => navigate(-1)} className="me-4 fs-5 cursor-pointer" />
            }
        >
            <Stack className="pt-3 overflow-auto" style={{ height: '80vh' }}>
                {isLoading ? (
                    <div className="d-flex justify-content-center mt-5">
                        <Spinner animation="border" variant="info" role="status" />
                    </div>
                ) : (
                    <Container className="mt-3">
                        <Row className="pb-3">
                            <Col md={firstCol}>PIN Length <span className="text-danger">*</span></Col>
                            <Col md={secondCol}>
                                <input 
                                    type="number" 
                                    min={4} 
                                    max={10} 
                                    value={pinLength}
                                    onChange={handlePinLengthChange}
                                    disabled={!canEditEstablishments}
                                    className={`form-control mt-1 ms-0 w-100 ${errors.pinLength ? 'is-invalid' : ''}`}
                                />
                                {errors.pinLength && <div className="invalid-feedback" style={{ display: 'block', color: '#DC3245' }}>{errors.pinLength}</div>}
                            </Col>
                        </Row>

                        <Row className="pb-3">
                            <Col md={firstCol} className="text-nowrap">Max Generation Attempt <span className="text-danger">*</span></Col>
                            <Col md={secondCol}>
                                <input 
                                    type="number" 
                                    min={1} 
                                    value={maxGenerationAttempt}
                                    onChange={handleMaxGenerationAttemptChange}
                                    disabled={!canEditEstablishments}
                                    className={`form-control mt-1 ms-0 w-100 ${errors.maxGenerationAttempt ? 'is-invalid' : ''}`}
                                />
                                {errors.maxGenerationAttempt && <div className="invalid-feedback" style={{ display: 'block', color: '#DC3245' }}>{errors.maxGenerationAttempt}</div>}
                            </Col>
                        </Row>

                        <Row className="pb-3">
                            <Col md={firstCol}>Auto PIN Generation</Col>
                            <Col md={secondCol}>
                                <input 
                                    type="checkbox" 
                                    checked={autoPinGeneration}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoPinGeneration(e.target.checked)}
                                    disabled={!canEditEstablishments}
                                    className="ms-0"
                                />
                            </Col>
                        </Row>

                        {
                            !saveMutation.isPending
                                ? (
                                    <Row className="mt-3">
                                        <Col md={firstCol}></Col>
                                        <Col md={secondCol}>
                                            <MISButton 
                                                text="Cancel" 
                                                className="btn popup-btn right-margin10 btn-outline-secondary" 
                                                type="button"
                                                FnOnClick={handleCancel}
                                            />
                                            <MISButton 
                                                text="Save" 
                                                className="popup-btn btn btn-primary crbc-bg-color" 
                                                type="button"
                                                FnOnClick={handleSave}
                                            />
                                        </Col>
                                    </Row>
                                )
                                : (
                                    <Row>
                                        <Col md={firstCol}></Col>
                                        <Col md={secondCol}>
                                            <div className="d-flex justify-content-center mt-3">
                                                <Spinner animation="border" variant="info" role="status" />
                                            </div>
                                        </Col>
                                    </Row>
                                )
                        }
                    </Container>
                )}
            </Stack>
        </Layout>
    );
}
